import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function TraderOnboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [trades, setTrades] = useState([]);
  const [selected, setSelected] = useState([]);
  const [postcode, setPostcode] = useState(user?.postcode || "");
  const [range, setRange] = useState(user?.work_range_miles ?? 15);
  const [businessName, setBusinessName] = useState(user?.business_name || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/meta/trades").then(({ data }) => setTrades(data || []));
    setSelected(user?.trades || []);
  }, [user]);

  const toggle = (slug) => {
    setSelected((s) => {
      if (s.includes(slug)) return s.filter((x) => x !== slug);
      if (s.length >= 8) {
        toast.error("You can pick up to 8 services. Choose the ones you're most confident with.");
        return s;
      }
      return [...s, slug];
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (selected.length === 0) { toast.error("Pick at least one trade"); return; }
    if (!postcode) { toast.error("Enter your postcode"); return; }
    setBusy(true);
    try {
      await api.patch("/me/profile", {
        trades: selected,
        postcode,
        work_range_miles: Number(range),
        business_name: businessName,
        bio,
      });
      await refresh();
      toast.success("Profile saved");
      navigate("/subscribe");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-5 py-10">
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl" style={{ color: "var(--brand-green)" }}>Tradesperson setup</h1>
        <p className="text-[var(--text-muted)] mt-2">Tell us a bit about your work — this powers your job-alert matching.</p>

        <form onSubmit={submit} className="mt-6 brand-card p-6 space-y-5">
          <label className="block">
            <span className="text-sm font-semibold text-[var(--text-muted)]">Business name (optional)</span>
            <input
              data-testid="onb-business-input"
              value={businessName} onChange={(e) => setBusinessName(e.target.value)}
              className="w-full mt-1 px-3 py-2.5 rounded-md border border-[var(--border-stone)] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--brand-orange)]"
              placeholder="e.g. Tom's Plumbing Co."
            />
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-[var(--text-muted)]">Short bio</span>
            <textarea
              data-testid="onb-bio-input"
              rows={3} value={bio} onChange={(e) => setBio(e.target.value)}
              className="w-full mt-1 px-3 py-2.5 rounded-md border border-[var(--border-stone)] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--brand-orange)] resize-y"
              placeholder="Briefly describe your experience and specialty."
            />
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm font-semibold text-[var(--text-muted)]">Your postcode *</span>
              <input
                data-testid="onb-postcode-input"
                required value={postcode} onChange={(e) => setPostcode(e.target.value)}
                placeholder="e.g. M14 5AB"
                className="w-full mt-1 px-3 py-2.5 rounded-md border border-[var(--border-stone)] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--brand-orange)] uppercase"
              />
            </label>
            <label className="block">
              <span className="text-sm font-semibold text-[var(--text-muted)]">Work range: {range} mile{range===1?"":"s"}</span>
              <input
                data-testid="onb-range-slider"
                type="range" min={0} max={50} step={1} value={range}
                onChange={(e) => setRange(Number(e.target.value))}
                className="w-full mt-3 accent-[var(--brand-orange)]"
              />
            </label>
          </div>

          <div>
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-semibold text-[var(--text-muted)]">Trades you offer *</span>
              <span className="text-xs text-[var(--text-muted)]">{selected.length}/8 selected</span>
            </div>
            <div className="mt-2 flex flex-wrap gap-2" data-testid="onb-trades-grid">
              {trades.map((t) => {
                const active = selected.includes(t.slug);
                return (
                  <button
                    type="button" key={t.slug}
                    onClick={() => toggle(t.slug)}
                    data-testid={`onb-trade-${t.slug}`}
                    className={`px-3 py-1.5 rounded-md text-sm font-semibold border transition-colors ${
                      active
                        ? "bg-[var(--brand-green)] text-white border-[var(--brand-green)]"
                        : "bg-white text-[var(--text-muted)] border-[var(--border-stone)] hover:border-[var(--brand-green)]"
                    }`}
                  >{t.name}</button>
                );
              })}
            </div>
          </div>

          <button type="submit" disabled={busy} className="btn-accent w-full justify-center" data-testid="onb-submit-btn">
            {busy ? "Saving…" : "Save & continue to subscribe"}
          </button>
        </form>
      </div>
    </Layout>
  );
}
